import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { 
  Vote, 
  Plus, 
  Search,
  Filter,
  Calendar,
  Users,
  MoreVertical,
  Eye,
  Play,
  Square,
  BarChart3,
  Key
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger 
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Layout } from "../components/Layout";
import { getConsultes, activateConsulta, closeConsulta } from "../lib/api";
import { toast } from "sonner";
import { formatDateTz, formatLocalDateTime } from "../lib/dateUtils";

export default function ConsultesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [consultes, setConsultes] = useState([]);
  const [filteredConsultes, setFilteredConsultes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    loadConsultes();
  }, []);

  useEffect(() => {
    filterConsultes();
  }, [searchTerm, statusFilter, consultes]);

  const loadConsultes = async () => {
    try {
      const response = await getConsultes();
      setConsultes(response.data);
    } catch (error) {
      toast.error(t('consultes.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const filterConsultes = () => {
    let filtered = consultes;

    if (searchTerm) {
      filtered = filtered.filter(c => 
        c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      if (statusFilter === "voting") {
        const now = new Date();
        filtered = filtered.filter(c =>
          c.status === "published" && c.start_date && c.end_date &&
          now >= new Date(c.start_date) && now <= new Date(c.end_date)
        );
      } else {
        filtered = filtered.filter(c => c.status === statusFilter);
      }
    }

    setFilteredConsultes(filtered);
  };

  const handleActivate = async (id) => {
    try {
      await activateConsulta(id);
      toast.success(t('consultes.published'));
      loadConsultes();
    } catch (error) {
      toast.error(t('consultaDetail.publishError'));
    }
  };

  const handleClose = async (id) => {
    try {
      await closeConsulta(id);
      toast.success(t('consultes.closed'));
      loadConsultes();
    } catch (error) {
      toast.error(t('consultes.closeError'));
    }
  };

  const getStatusBadge = (status, consulta) => {
    const isVoting = status === "published" && consulta?.start_date && consulta?.end_date &&
      new Date() >= new Date(consulta.start_date) && new Date() <= new Date(consulta.end_date);
    
    if (isVoting) {
      return <span className="status-badge status-active">{t('consultes.statusVoting')}</span>;
    }

    // Show "Pendent Blockchain" when waiting for blockchain writes
    if (status === "pending_blockchain") {
      return <span className="status-badge status-pending-blockchain" data-testid="status-pending-blockchain">{t('status.pendingBlockchain')}</span>;
    }

    // Show "Pendent de Clau" when admin approved
    if (status === "closed" && consulta?.scrutiny_request_status === "admin_approved") {
      return <span className="status-badge status-pending-key" data-testid="status-pending-key">{t('status.pendingKey')}</span>;
    }

    // Show "Escrutini Sol·licitat" when closed with pending scrutiny request
    if (status === "closed" && consulta?.scrutiny_request_status) {
      return <span className="status-badge status-scrutiny-requested" data-testid="status-scrutiny-requested">{t('status.scrutinyRequested')}</span>;
    }
    
    const badges = {
      draft: { class: "status-draft", label: t('consultes.statusDraft') },
      pending_publication: { class: "status-pending", label: t('consultes.statusPendingPub') },
      published: { class: "status-pending", label: t('consultes.statusPublished') },
      pending_closure: { class: "status-closed", label: t('consultes.statusPendingClose') },
      closed: { class: "status-closed", label: t('consultes.statusClosed') },
      expired: { class: "status-draft", label: t('consultes.statusExpired') },
      scrutinized: { class: "status-scrutinized", label: t('consultes.statusScrutinized') }
    };
    const badge = badges[status] || badges.draft;
    return <span className={`status-badge ${badge.class}`}>{badge.label}</span>;
  };

  return (
    <Layout>
      <div className="content-container" data-testid="consultes-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8"
        >
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900">
              {t('consultes.title')}
            </h1>
            <p className="text-slate-500 mt-1">
              {t('consultes.subtitle')}
            </p>
          </div>
          <Link to="/consultes/new">
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="create-consulta-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t('consultes.newConsulta')}
            </Button>
          </Link>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-col md:flex-row gap-4 mb-6"
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder={t('consultes.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-consultes"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder={t('common.status')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('consultes.allStatuses')}</SelectItem>
              <SelectItem value="draft">{t('consultes.statusDraft')}</SelectItem>
              <SelectItem value="pending_publication">{t('consultes.statusPendingPub')}</SelectItem>
              <SelectItem value="published">{t('consultes.statusPublished')}</SelectItem>
              <SelectItem value="voting">{t('consultes.statusVoting')}</SelectItem>
              <SelectItem value="closed">{t('consultes.statusClosed')}</SelectItem>
              <SelectItem value="scrutinized">{t('consultes.statusScrutinized')}</SelectItem>
            </SelectContent>
          </Select>
        </motion.div>

        {/* Consultes List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : filteredConsultes.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="empty-state bg-white border border-slate-200 rounded-lg py-16"
          >
            <Vote className="empty-state-icon" />
            <h3 className="empty-state-title">{t('consultes.noConsultes')}</h3>
            <p className="empty-state-description">
              {searchTerm || statusFilter !== "all" 
                ? t('consultes.noFilterResults')
                : t('consultes.createFirst')}
            </p>
            {!searchTerm && statusFilter === "all" && (
              <Link to="/consultes/new">
                <Button data-testid="empty-create-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('consultes.createConsulta')}
                </Button>
              </Link>
            )}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white border border-slate-200 rounded-lg overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('consultes.titleColumn')}</th>
                    <th>{t('consultes.datesColumn')}</th>
                    <th>{t('consultes.optionsColumn')}</th>
                    <th>{t('common.status')}</th>
                    <th className="w-20">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredConsultes.map((consulta) => (
                    <tr
                      key={consulta.id}
                      data-testid={`consulta-row-${consulta.id}`}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/consultes/${consulta.id}`)}
                    >                      <td>
                        <Link 
                          to={`/consultes/${consulta.id}`}
                          className="font-medium text-slate-900 hover:text-blue-600"
                        >
                          {consulta.title}
                        </Link>
                      </td>
                      <td>
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Calendar className="h-4 w-4" />
                          <span>
                            {formatLocalDateTime(consulta.start_date, consulta.entity_timezone)} - {" "}
                            {formatLocalDateTime(consulta.end_date, consulta.entity_timezone)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="text-sm text-slate-600">
                          {consulta.options.length} {t('consultes.options')}
                        </span>
                      </td>
                      <td>{getStatusBadge(consulta.status, consulta)}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              data-testid={`consulta-actions-${consulta.id}`}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to={`/consultes/${consulta.id}`}>
                                <Eye className="h-4 w-4 mr-2" />
                                {t('consultes.viewDetails')}
                              </Link>
                            </DropdownMenuItem>
                            {consulta.status === "draft" && (
                              <>
                                <DropdownMenuItem asChild>
                                  <Link to={`/consultes/${consulta.id}/census`}>
                                    <Users className="h-4 w-4 mr-2" />
                                    {t('consultes.loadCensus')}
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleActivate(consulta.id)}
                                  data-testid={`activate-${consulta.id}`}
                                >
                                  <Play className="h-4 w-4 mr-2" />
                                  {t('consultes.publish')}
                                </DropdownMenuItem>
                              </>
                            )}
                            {consulta.status === "published" && (
                              <DropdownMenuItem 
                                onClick={() => handleClose(consulta.id)}
                                data-testid={`close-${consulta.id}`}
                              >
                                <Square className="h-4 w-4 mr-2" />
                                {t('consultes.closeVoting')}
                              </DropdownMenuItem>
                            )}
                            {consulta.status === "closed" && !consulta.scrutiny_request_status && (
                              <DropdownMenuItem asChild>
                                <Link to={`/consultes/${consulta.id}/scrutiny`}>
                                  <BarChart3 className="h-4 w-4 mr-2" />
                                  {t('consultes.doScrutiny')}
                                </Link>
                              </DropdownMenuItem>
                            )}
                            {consulta.status === "closed" && consulta.scrutiny_request_status === "admin_approved" && consulta.scrutiny_request_token && (
                              <DropdownMenuItem asChild>
                                <Link to={`/scrutiny/complete/${consulta.scrutiny_request_token}`}>
                                  <Key className="h-4 w-4 mr-2" />
                                  {t('consultes.enterKey')}
                                </Link>
                              </DropdownMenuItem>
                            )}
                            {consulta.status === "scrutinized" && (
                              <DropdownMenuItem asChild>
                                <Link to={`/consultes/${consulta.id}/results`}>
                                  <BarChart3 className="h-4 w-4 mr-2" />
                                  {t('scrutiny.viewResults')}
                                </Link>
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </div>
    </Layout>
  );
}
